import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ClipboardCheck, User, FileText, Shield, CheckCircle, AlertCircle, Download, CreditCard, ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WorkerOnboarding = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [pdfForms, setPdfForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [showPdfFormDialog, setShowPdfFormDialog] = useState(false);
  const [selectedPdfForm, setSelectedPdfForm] = useState(null);
  const [pdfFormResponses, setPdfFormResponses] = useState({});
  // Custom PDF Forms (Smart Form Builder)
  const [customForms, setCustomForms] = useState([]);
  const [loadingCustomForms, setLoadingCustomForms] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    ssn: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    previousEmployer: '',
    yearsOfExperience: '',
    certifications: '',
    safetyTraining: '',
    medicalConditions: '',
    // Direct Deposit fields
    bankName: '',
    accountType: 'checking',
    routingNumber: '',
    accountNumber: '',
    confirmAccountNumber: ''
  });
  
  const [idUploads, setIdUploads] = useState([]);
  const [currentIdType, setCurrentIdType] = useState('');
  const [currentIdFiles, setCurrentIdFiles] = useState(null);

  const checkOnboardingStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/user/onboarding-status');
      setOnboardingStatus(response.data);
      
      // If already completed, pre-fill form
      if (response.data.completed) {
        setFormData(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (error) {
      console.error('Failed to load onboarding status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPdfForms = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/my-pdf-forms');
      // Filter to show only new hire forms (I-9, W-4, etc.)
      const newHireForms = response.data.filter(form => 
        form.title && (
          form.title.includes('I-9') || 
          form.title.includes('W-4') || 
          form.title.includes('W4') ||
          form.title.includes('IT-4') ||
          form.title.toLowerCase().includes('withholding') ||
          form.title.toLowerCase().includes('employment eligibility')
        )
      );
      setPdfForms(newHireForms);
    } catch (error) {
      console.error('Failed to load PDF forms:', error);
    } finally {
      setLoadingForms(false);
    }
  }, []);

  // Load custom PDF forms uploaded by admin (Smart Form Builder)
  const loadCustomForms = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/worker/required-forms');
      setCustomForms(response.data);
    } catch (error) {
      console.error('Failed to load custom forms:', error);
    } finally {
      setLoadingCustomForms(false);
    }
  }, []);

  useEffect(() => {
    checkOnboardingStatus();
    loadPdfForms();
    loadCustomForms();
  }, [checkOnboardingStatus, loadPdfForms, loadCustomForms]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openPdfFormDialog = (pdfForm) => {
    setSelectedPdfForm(pdfForm);
    setPdfFormResponses({});
    setShowPdfFormDialog(true);
  };

  const handlePdfFormResponseChange = (fieldName, value) => {
    setPdfFormResponses(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const submitPdfForm = async () => {
    try {
      // Submit form data and get filled PDF
      const response = await axiosInstance.post(`/pdf-forms/${selectedPdfForm.id}/fill-and-download`, {
        responses: pdfFormResponses
      }, {
        responseType: 'blob'
      });

      // Create blob link to download filled PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedPdfForm.title}_filled.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Form submitted successfully! Your filled PDF has been downloaded.');
      setPdfFormResponses({});
      setShowPdfFormDialog(false);
      loadPdfForms(); // Reload to update status
    } catch (error) {
      console.error('Failed to submit PDF form:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    }
  };

  const downloadPdfTemplate = async (formId) => {
    try {
      const response = await axiosInstance.get(`/pdf-forms/${formId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'pdf-template.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF template downloaded successfully!');
    } catch (error) {
      console.error('Failed to download PDF template:', error);
      toast.error('Failed to download PDF template');
    }
  };

  const renderPdfFormField = (fieldName, fieldLabel) => {
    const value = pdfFormResponses[fieldName] || '';
    
    if (fieldName.toLowerCase().includes('ssn') || fieldName.toLowerCase().includes('social security')) {
      return (
        <Input
          type="text"
          placeholder="XXX-XX-XXXX"
          value={value}
          onChange={(e) => handlePdfFormResponseChange(fieldName, e.target.value)}
          maxLength={11}
        />
      );
    } else if (fieldName.toLowerCase().includes('address')) {
      return (
        <Textarea
          placeholder="Enter address"
          value={value}
          onChange={(e) => handlePdfFormResponseChange(fieldName, e.target.value)}
          rows={2}
        />
      );
    } else if (fieldName.toLowerCase().includes('marital') || fieldName.toLowerCase().includes('status')) {
      return (
        <Select
          value={value}
          onValueChange={(selectedValue) => handlePdfFormResponseChange(fieldName, selectedValue)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single">Single or Married filing separately</SelectItem>
            <SelectItem value="married_joint">Married filing jointly or Qualifying surviving spouse</SelectItem>
            <SelectItem value="head_of_household">Head of household</SelectItem>
          </SelectContent>
        </Select>
      );
    } else if (fieldName.toLowerCase().includes('date')) {
      // Format date as MM/DD/YYYY for display
      const formatDateForDisplay = (dateValue) => {
        if (!dateValue) return '';
        // If already in MM/DD/YYYY format, return as is
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) return dateValue;
        // Convert from YYYY-MM-DD to MM/DD/YYYY
        const parts = dateValue.split('-');
        if (parts.length === 3) {
          return `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        return dateValue;
      };
      
      const handleDateChange = (inputValue) => {
        // Allow typing in MM/DD/YYYY format
        let cleaned = inputValue.replace(/[^\d/]/g, '');
        // Auto-add slashes
        if (cleaned.length === 2 && !cleaned.includes('/')) {
          cleaned = cleaned + '/';
        } else if (cleaned.length === 5 && cleaned.split('/').length === 2) {
          cleaned = cleaned + '/';
        }
        // Limit to MM/DD/YYYY format (10 chars)
        if (cleaned.length <= 10) {
          handlePdfFormResponseChange(fieldName, cleaned);
        }
      };
      
      return (
        <Input
          type="text"
          placeholder="MM/DD/YYYY"
          value={formatDateForDisplay(value)}
          onChange={(e) => handleDateChange(e.target.value)}
          maxLength={10}
        />
      );
    } else {
      return (
        <Input
          placeholder={`Enter ${fieldLabel.toLowerCase()}`}
          value={value}
          onChange={(e) => handlePdfFormResponseChange(fieldName, e.target.value)}
        />
      );
    }
  };

  const handleUploadCombinedInfo = async () => {
    // Upload Personal + Emergency + Employment + Medical as one PDF
    const combinedData = {
      fullName: formData.fullName,
      dateOfBirth: formData.dateOfBirth,
      ssn: formData.ssn,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      phone: formData.phone,
      emergencyContactName: formData.emergencyContactName,
      emergencyContactPhone: formData.emergencyContactPhone,
      emergencyContactRelationship: formData.emergencyContactRelationship,
      previousEmployer: formData.previousEmployer,
      yearsOfExperience: formData.yearsOfExperience,
      certifications: formData.certifications,
      safetyTraining: formData.safetyTraining,
      medicalConditions: formData.medicalConditions,
    };

    try {
      setSubmitting(true);
      await axiosInstance.post('/user/onboarding/upload-combined-info', combinedData);
      toast.success('Personal & Employment Information PDF uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddToUploadBatch = () => {
    // Add current selection to batch (doesn't upload yet)
    if (!currentIdFiles || currentIdFiles.length === 0) {
      toast.error('Please select files');
      return;
    }

    if (!currentIdType) {
      toast.error('Please select document type');
      return;
    }

    // Add to batch
    setIdUploads(prev => [
      ...prev,
      {
        type: currentIdType,
        fileCount: currentIdFiles.length,
        files: Array.from(currentIdFiles)
      }
    ]);

    toast.success(`${currentIdType.replace(/_/g, ' ')} added to upload batch`);
    
    // Clear the form for next ID type
    setCurrentIdType('');
    setCurrentIdFiles(null);
    document.getElementById('idFiles').value = '';
  };

  const handleUploadAllIDs = async () => {
    if (idUploads.length === 0) {
      toast.error('Please add documents to upload first');
      return;
    }

    try {
      setSubmitting(true);
      let totalUploaded = 0;
      
      // Upload all batched documents
      for (const batch of idUploads) {
        for (const file of batch.files) {
          const formDataToSend = new FormData();
          formDataToSend.append('file', file);
          formDataToSend.append('idType', batch.type);

          await axiosInstance.post('/id-uploads', formDataToSend, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          totalUploaded++;
        }
      }

      toast.success(`✅ ${totalUploaded} document(s) uploaded successfully!`);
      
      // Keep the list visible to show what was uploaded, but mark as uploaded
      // You could also clear it: setIdUploads([]);
    } catch (error) {
      console.error('Failed to upload files:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload files');
    } finally {
      setSubmitting(false);
    }
  };

  // Note: This function is no longer used since we removed the submit button
  // Workers now upload sections individually using the upload buttons
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Form submission removed - workers upload sections individually
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            className="mb-4 hover:bg-gray-100"
          >
            ← {t('common.back')}
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{t('onboarding.title')}</h1>
          <p className="text-gray-600 mt-1">{t('onboarding.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-6 h-6 text-blue-600" />
                  {t('onboarding.personalInfo')}
                </CardTitle>
                <CardDescription>{t('onboarding.personalInfoDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">{t('onboarding.fullLegalName')} *</Label>
                    <Input
                      id="fullName"
                      required
                      value={formData.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                      placeholder="John Michael Doe"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth">{t('onboarding.dateOfBirth')} *</Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      required
                      value={formData.dateOfBirth}
                      onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ssn">{t('onboarding.socialSecurity')} *</Label>
                    <Input
                      id="ssn"
                      required
                      value={formData.ssn}
                      onChange={(e) => handleChange('ssn', e.target.value)}
                      placeholder="XXX-XX-XXXX"
                      type="password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('onboarding.phoneNumber')} *</Label>
                    <Input
                      id="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('onboarding.emailAddress')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="john.doe@email.com"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">{t('onboarding.streetAddress')} *</Label>
                    <Input
                      id="address"
                      required
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      placeholder="123 Main Street, Apt 4B"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">{t('onboarding.city')} *</Label>
                    <Input
                      id="city"
                      required
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">{t('onboarding.state')} *</Label>
                    <Input
                      id="state"
                      required
                      value={formData.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      placeholder="CA"
                      maxLength={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zipCode">{t('onboarding.zipCode')} *</Label>
                    <Input
                      id="zipCode"
                      required
                      value={formData.zipCode}
                      onChange={(e) => handleChange('zipCode', e.target.value)}
                      placeholder="12345"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  {t('onboarding.emergencyContact')}
                </CardTitle>
                <CardDescription>{t('onboarding.emergencyContactDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergencyContactName">{t('onboarding.contactName')} *</Label>
                    <Input
                      id="emergencyContactName"
                      required
                      value={formData.emergencyContactName}
                      onChange={(e) => handleChange('emergencyContactName', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emergencyContactPhone">{t('onboarding.contactPhone')} *</Label>
                    <Input
                      id="emergencyContactPhone"
                      required
                      value={formData.emergencyContactPhone}
                      onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="emergencyContactRelationship">{t('onboarding.relationship')} *</Label>
                    <Input
                      id="emergencyContactRelationship"
                      required
                      value={formData.emergencyContactRelationship}
                      onChange={(e) => handleChange('emergencyContactRelationship', e.target.value)}
                      placeholder="Spouse, Parent, Sibling, etc."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Employment History */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-6 h-6 text-purple-600" />
                  Employment & Training History
                </CardTitle>
                <CardDescription>Previous experience and qualifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="previousEmployer">{t('onboarding.previousEmployer')}</Label>
                    <Input
                      id="previousEmployer"
                      value={formData.previousEmployer}
                      onChange={(e) => handleChange('previousEmployer', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="yearsOfExperience">{t('onboarding.yearsExperience')}</Label>
                    <Input
                      id="yearsOfExperience"
                      type="number"
                      value={formData.yearsOfExperience}
                      onChange={(e) => handleChange('yearsOfExperience', e.target.value)}
                      placeholder="0"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="certifications">{t('onboarding.certifications')}</Label>
                    <Textarea
                      id="certifications"
                      value={formData.certifications}
                      onChange={(e) => handleChange('certifications', e.target.value)}
                      placeholder="OSHA 10, OSHA 30, First Aid, CPR, etc."
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="safetyTraining">{t('onboarding.previousTraining')}</Label>
                    <Textarea
                      id="safetyTraining"
                      value={formData.safetyTraining}
                      onChange={(e) => handleChange('safetyTraining', e.target.value)}
                      placeholder="List any safety training you've completed"
                      rows={3}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Medical & Safety */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-6 h-6 text-green-600" />
                  Medical & Safety Information
                </CardTitle>
                <CardDescription>Health and safety disclosures</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="medicalConditions">{t('onboarding.medicalConditions')}</Label>
                  <Textarea
                    id="medicalConditions"
                    value={formData.medicalConditions}
                    onChange={(e) => handleChange('medicalConditions', e.target.value)}
                    placeholder="Any medical conditions, allergies, or physical restrictions we should be aware of"
                    rows={4}
                  />
                  <p className="text-xs text-gray-500">
                    This information is confidential and used only for safety purposes
                  </p>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      ℹ️ Clicking this button will <strong>automatically generate a PDF</strong> combining all sections above (Personal Info, Emergency Contact, Employment, Medical) and upload it to the admin dashboard.
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={handleUploadCombinedInfo}
                      disabled={submitting || !formData.fullName || !formData.dateOfBirth || !formData.ssn || !formData.phone}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Generate & Upload Personal/Employment PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Direct Deposit Information */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                  Direct Deposit Information
                </CardTitle>
                <CardDescription>Set up direct deposit for your payroll</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <p className="text-sm text-purple-800">
                      ℹ️ Direct deposit allows your pay to be electronically transferred to your bank account. Your information is encrypted and secure.
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bankName">{t('onboarding.bankName')}</Label>
                      <Input
                        id="bankName"
                        value={formData.bankName}
                        onChange={(e) => handleChange('bankName', e.target.value)}
                        placeholder="e.g., Chase Bank, Wells Fargo"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="accountType">{t('onboarding.accountType')}</Label>
                      <Select
                        value={formData.accountType}
                        onValueChange={(value) => handleChange('accountType', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">{t('onboarding.checking')}</SelectItem>
                          <SelectItem value="savings">{t('onboarding.savings')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="routingNumber">{t('onboarding.routingNumber')}</Label>
                      <Input
                        id="routingNumber"
                        value={formData.routingNumber}
                        onChange={(e) => handleChange('routingNumber', e.target.value)}
                        placeholder="9-digit routing number"
                        maxLength={9}
                      />
                      <p className="text-xs text-gray-500">
                        Found on the bottom left of your check
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="accountNumber">{t('onboarding.accountNumber')}</Label>
                      <Input
                        id="accountNumber"
                        type="password"
                        value={formData.accountNumber}
                        onChange={(e) => handleChange('accountNumber', e.target.value)}
                        placeholder="Account number"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmAccountNumber">{t('onboarding.confirmAccountNumber')}</Label>
                    <Input
                      id="confirmAccountNumber"
                      type="password"
                      value={formData.confirmAccountNumber}
                      onChange={(e) => handleChange('confirmAccountNumber', e.target.value)}
                      placeholder="Re-enter account number"
                    />
                  </div>
                  
                  {formData.accountNumber && formData.confirmAccountNumber && formData.accountNumber !== formData.confirmAccountNumber && (
                    <p className="text-sm text-red-600">
                      Account numbers do not match
                    </p>
                  )}
                  
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={async () => {
                        if (!formData.bankName || !formData.routingNumber || !formData.accountNumber) {
                          toast.error('Please fill in all direct deposit fields');
                          return;
                        }
                        if (formData.accountNumber !== formData.confirmAccountNumber) {
                          toast.error('Account numbers do not match');
                          return;
                        }
                        if (formData.routingNumber.length !== 9) {
                          toast.error('Routing number must be 9 digits');
                          return;
                        }
                        
                        try {
                          await axiosInstance.post('/user/onboarding/direct-deposit', {
                            bankName: formData.bankName,
                            accountType: formData.accountType,
                            routingNumber: formData.routingNumber,
                            accountNumber: formData.accountNumber
                          });
                          toast.success('Direct deposit information saved!');
                        } catch (error) {
                          console.error('Failed to save direct deposit:', error);
                          toast.error('Failed to save direct deposit information');
                        }
                      }}
                      className="bg-purple-600 hover:bg-purple-700"
                      disabled={!formData.bankName || !formData.routingNumber || !formData.accountNumber || formData.accountNumber !== formData.confirmAccountNumber}
                    >
                      Save Direct Deposit Info
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Identification Documents */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-6 h-6 text-indigo-600" />
                  Identification Documents
                </CardTitle>
                <CardDescription>Add multiple ID types, then upload them all together</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Show batched items (not uploaded yet) */}
                  {idUploads.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                      <p className="font-medium text-blue-800">📋 Ready to Upload ({idUploads.reduce((sum, u) => sum + u.fileCount, 0)} files):</p>
                      {idUploads.map((upload, index) => (
                        <div key={upload.type || `upload-${index}`} className="text-sm text-blue-700">
                          <strong>{upload.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {upload.fileCount} file(s) - {upload.files.map(f => f.name).join(', ')}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new ID type to batch */}
                  <div className="space-y-2">
                    <Label htmlFor="currentIdType">{t('onboarding.documentType')} *</Label>
                    <Select
                      value={currentIdType}
                      onValueChange={(value) => setCurrentIdType(value)}
                    >
                      <SelectTrigger id="currentIdType">
                        <SelectValue placeholder="Select document type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="drivers_license">{t('onboarding.driversLicense')}</SelectItem>
                        <SelectItem value="state_id">{t('onboarding.stateId')}</SelectItem>
                        <SelectItem value="passport">{t('onboarding.passport')}</SelectItem>
                        <SelectItem value="military_id">{t('onboarding.militaryId')}</SelectItem>
                        <SelectItem value="birth_certificate">{t('onboarding.birthCertificate')}</SelectItem>
                        <SelectItem value="social_security_card">{t('onboarding.socialSecurityCard')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="idFiles">Select Files for This Document Type *</Label>
                    <Input
                      id="idFiles"
                      type="file"
                      accept="image/*,.pdf"
                      multiple
                      onChange={(e) => setCurrentIdFiles(e.target.files)}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-500">
                      <strong>Hold Ctrl (Windows) or Cmd (Mac)</strong> to select multiple files (front/back, etc.)
                    </p>
                  </div>

                  {currentIdFiles && currentIdFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium text-gray-700">{currentIdFiles.length} file(s) selected:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {Array.from(currentIdFiles).map((file, index) => (
                          <li key={`${file.name}-${file.size}`} className="flex items-center gap-2">
                            <span className="text-green-600">✓</span>
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-between gap-2">
                    <Button
                      type="button"
                      onClick={handleAddToUploadBatch}
                      disabled={!currentIdType || !currentIdFiles || currentIdFiles.length === 0}
                      variant="outline"
                      className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                    >
                      + Add to Batch
                    </Button>
                    
                    {idUploads.length > 0 && (
                      <Button
                        type="button"
                        onClick={handleUploadAllIDs}
                        disabled={submitting}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {submitting ? 'Uploading...' : `Upload All (${idUploads.reduce((sum, u) => sum + u.fileCount, 0)} files)`}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Required Forms (Smart Form Builder) */}
            <Card className="mb-6 border-l-4 border-l-purple-600">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-6 h-6 text-purple-600" />
                  Company Required Forms
                </CardTitle>
                <CardDescription>Complete the following forms uploaded by your company</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingCustomForms ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  </div>
                ) : customForms.length > 0 ? (
                  <div className="space-y-3">
                    {/* Pending Forms */}
                    {customForms.filter(f => !f.completed).length > 0 && (
                      <>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Required ({customForms.filter(f => !f.completed).length})</h4>
                        {customForms.filter(f => !f.completed).map((form) => (
                          <div key={form.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 border-l-4 border-l-red-400">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900">{form.formName}</h4>
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Required</span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                {form.state ? `${form.state} State Form` : 'Federal Form'} • {form.category} • {form.fields?.length || 0} fields
                              </p>
                            </div>
                            <Button
                              type="button"
                              onClick={() => navigate(`/worker/forms/${form.id}/fill`)}
                              className="ml-4 bg-purple-600 hover:bg-purple-700"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Fill Out Form
                            </Button>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Completed Forms */}
                    {customForms.filter(f => f.completed).length > 0 && (
                      <>
                        <h4 className="text-sm font-semibold text-gray-700 mt-4 mb-2">Completed ({customForms.filter(f => f.completed).length})</h4>
                        {customForms.filter(f => f.completed).map((form) => (
                          <div key={form.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 border-l-4 border-l-green-400">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900">{form.formName}</h4>
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Completed
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                {form.state ? `${form.state} State Form` : 'Federal Form'} • {form.category}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">Need to update? Click "Update Form" to submit new information.</p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => navigate(`/worker/forms/${form.id}/fill`)}
                              className="ml-4"
                            >
                              Update Form
                            </Button>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No company forms assigned yet</p>
                    <p className="text-sm mt-1">Your administrator will assign forms when ready</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </form>
      </main>

      {/* PDF Form Dialog */}
      <Dialog open={showPdfFormDialog} onOpenChange={setShowPdfFormDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fill Out PDF Form</DialogTitle>
            <DialogDescription>
              {selectedPdfForm && (
                <>
                  Complete "{selectedPdfForm.title}" form
                  {selectedPdfForm.description && <div className="mt-2 text-sm">{selectedPdfForm.description}</div>}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedPdfForm && (
            <div className="mb-4">
              <Button
                variant="outline"
                onClick={() => downloadPdfTemplate(selectedPdfForm.id)}
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF Template (Fill Offline)
              </Button>
            </div>
          )}

          {selectedPdfForm && (
            <div className="space-y-6 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> Fill out the form fields below, OR download the PDF template and submit the completed form to your supervisor.
                </p>
              </div>

              {/* Federal W-4 Form - Show ONLY for Federal W-4, not MI-W4 */}
              {((selectedPdfForm.title.toLowerCase().includes('w-4') || selectedPdfForm.title.toLowerCase().includes('w4')) && 
                !selectedPdfForm.title.toLowerCase().includes('mi-w4') && 
                !selectedPdfForm.title.toLowerCase().includes('michigan')) && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold">Form W-4: Employee's Withholding Certificate</h3>
                  
                  {/* Step 1: Personal Information */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Step 1: Enter Personal Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Name and Middle Initial *</Label>
                        {renderPdfFormField('firstName', 'First Name')}
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name *</Label>
                        {renderPdfFormField('lastName', 'Last Name')}
                      </div>
                      <div className="space-y-2">
                        <Label>Social Security Number *</Label>
                        {renderPdfFormField('ssn', 'XXX-XX-XXXX')}
                      </div>
                      <div className="space-y-2">
                        <Label>Home Address (Street and Number)</Label>
                        {renderPdfFormField('address', 'Address')}
                      </div>
                      <div className="space-y-2">
                        <Label>City or Town, State, and ZIP Code</Label>
                        {renderPdfFormField('cityStateZip', 'City, State, ZIP')}
                      </div>
                      <div className="space-y-2">
                        <Label>Filing Status (Check one) *</Label>
                        <Select
                          value={pdfFormResponses['filingStatus'] || ''}
                          onValueChange={(value) => handlePdfFormResponseChange('filingStatus', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select filing status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single">Single or Married filing separately</SelectItem>
                            <SelectItem value="married">Married filing jointly</SelectItem>
                            <SelectItem value="head">Head of household</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Multiple Jobs */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Step 2: Multiple Jobs or Spouse Works (Optional)</h4>
                    <div className="space-y-2">
                      <Label>Check if you have more than one job or if married filing jointly and spouse also works</Label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={pdfFormResponses['multipleJobs'] === 'yes'}
                          onChange={(e) => handlePdfFormResponseChange('multipleJobs', e.target.checked ? 'yes' : 'no')}
                        />
                        <span className="text-sm">Check here if applicable</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Claim Dependents */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Step 3: Claim Dependents (Optional)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Number of Qualifying Children</Label>
                        {renderPdfFormField('qualifyingChildren', 'Number')}
                      </div>
                      <div className="space-y-2">
                        <Label>Number of Other Dependents</Label>
                        {renderPdfFormField('otherDependents', 'Number')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Total Credits (Multiply children by $2,000 and others by $500)</Label>
                        {renderPdfFormField('totalCredits', 'Amount')}
                      </div>
                    </div>
                  </div>

                  {/* Step 4: Other Adjustments */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Step 4: Other Adjustments (Optional)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Other Income (not from jobs)</Label>
                        {renderPdfFormField('otherIncome', '$0')}
                      </div>
                      <div className="space-y-2">
                        <Label>Deductions</Label>
                        {renderPdfFormField('deductions', '$0')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Extra Withholding (Additional amount per pay period)</Label>
                        {renderPdfFormField('extraWithholding', '$0')}
                      </div>
                    </div>
                  </div>

                  {/* Step 5: Sign Here */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Step 5: Sign Here</h4>
                    <div className="space-y-2">
                      <Label>Employee's Signature (Type your full name)</Label>
                      {renderPdfFormField('signature', 'Your Full Name')}
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label>Date</Label>
                      {renderPdfFormField('signatureDate', 'Date')}
                    </div>
                  </div>
                </div>
              )}

              {/* I-9 Form - Show if title contains "I-9" or "I9" */}
              {(selectedPdfForm.title.toLowerCase().includes('i-9') || selectedPdfForm.title.toLowerCase().includes('i9')) && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold">Form I-9: Employment Eligibility Verification</h3>
                  
                  {/* Section 1: Employee Information */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Section 1: Employee Information and Attestation</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Last Name (Family Name) *</Label>
                        {renderPdfFormField('lastName', 'Last Name')}
                      </div>
                      <div className="space-y-2">
                        <Label>First Name (Given Name) *</Label>
                        {renderPdfFormField('firstName', 'First Name')}
                      </div>
                      <div className="space-y-2">
                        <Label>Middle Initial</Label>
                        {renderPdfFormField('middleInitial', 'M.I.')}
                      </div>
                      <div className="space-y-2">
                        <Label>Other Last Names Used (if any)</Label>
                        {renderPdfFormField('maidenName', 'Other Names')}
                      </div>
                      <div className="space-y-2">
                        <Label>Address (Street Number and Name)</Label>
                        {renderPdfFormField('address', 'Street Address')}
                      </div>
                      <div className="space-y-2">
                        <Label>Apt. Number</Label>
                        {renderPdfFormField('aptNumber', 'Apt #')}
                      </div>
                      <div className="space-y-2">
                        <Label>City or Town</Label>
                        {renderPdfFormField('city', 'City')}
                      </div>
                      <div className="space-y-2">
                        <Label>State</Label>
                        {renderPdfFormField('state', 'State')}
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP Code</Label>
                        {renderPdfFormField('zipCode', 'ZIP')}
                      </div>
                      <div className="space-y-2">
                        <Label>Date of Birth (mm/dd/yyyy) *</Label>
                        {renderPdfFormField('dateOfBirth', 'Date')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Social Security Number *</Label>
                        {renderPdfFormField('ssn', 'XXX-XX-XXXX')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Email Address</Label>
                        {renderPdfFormField('email', 'Email')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Telephone Number</Label>
                        {renderPdfFormField('phone', 'Phone')}
                      </div>
                    </div>
                  </div>

                  {/* Citizenship Status */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Citizenship/Immigration Status</h4>
                    <div className="space-y-4">
                      <Label className="font-semibold">Citizenship/Immigration Status (Check one) *</Label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="citizenshipStatus"
                            value="citizen"
                            checked={pdfFormResponses['citizenshipStatus'] === 'citizen'}
                            onChange={(e) => handlePdfFormResponseChange('citizenshipStatus', e.target.value)}
                          />
                          <span className="text-sm">1. A citizen of the United States</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="citizenshipStatus"
                            value="national"
                            checked={pdfFormResponses['citizenshipStatus'] === 'national'}
                            onChange={(e) => handlePdfFormResponseChange('citizenshipStatus', e.target.value)}
                          />
                          <span className="text-sm">2. A noncitizen national of the United States</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="citizenshipStatus"
                            value="resident"
                            checked={pdfFormResponses['citizenshipStatus'] === 'resident'}
                            onChange={(e) => handlePdfFormResponseChange('citizenshipStatus', e.target.value)}
                          />
                          <span className="text-sm">3. A lawful permanent resident (Alien Registration Number/USCIS Number):</span>
                        </div>
                        {pdfFormResponses['citizenshipStatus'] === 'resident' && (
                          <div className="ml-6 mt-2">
                            <Label>Alien Registration Number/USCIS Number</Label>
                            {renderPdfFormField('uscisNumber', 'A-Number')}
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="citizenshipStatus"
                            value="authorized"
                            checked={pdfFormResponses['citizenshipStatus'] === 'authorized'}
                            onChange={(e) => handlePdfFormResponseChange('citizenshipStatus', e.target.value)}
                          />
                          <span className="text-sm">4. An alien authorized to work until (expiration date, if applicable):</span>
                        </div>
                        {pdfFormResponses['citizenshipStatus'] === 'authorized' && (
                          <div className="ml-6 mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label>Expiration Date</Label>
                              {renderPdfFormField('workAuthExpiration', 'mm/dd/yyyy')}
                            </div>
                            <div>
                              <Label>Alien Number or Form I-94 Number</Label>
                              {renderPdfFormField('i94Number', 'Number')}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <Label>Signature of Employee (Type your full name) *</Label>
                        {renderPdfFormField('employeeSignature', 'Your Full Name')}
                      </div>
                      <div>
                        <Label>Today's Date (mm/dd/yyyy) *</Label>
                        {renderPdfFormField('date', 'mm/dd/yyyy')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Michigan MI-W4 Form */}
              {(selectedPdfForm && (selectedPdfForm.title.toLowerCase().includes('mi-w4') || selectedPdfForm.title.toLowerCase().includes('michigan'))) && (
                <div className="space-y-6">
                <h3 className="text-xl font-bold">Michigan MI-W4: Employee's Withholding Exemption Certificate</h3>
                
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold mb-4">Employee Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>Social Security Number *</Label>
                      {renderPdfFormField('ssn', 'XXX-XX-XXXX')}
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Birth *</Label>
                      {renderPdfFormField('dateOfBirth', 'MM/DD/YYYY')}
                    </div>
                    <div className="space-y-2">
                      <Label>Driver License Number</Label>
                      {renderPdfFormField('driverLicense', 'License Number')}
                    </div>
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      {renderPdfFormField('firstName', 'First Name')}
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      {renderPdfFormField('lastName', 'Last Name')}
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold mb-4">Employment Status</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Are you a new employee? *</Label>
                      <select
                        value={pdfFormResponses['newEmployee'] || ''}
                        onChange={(e) => handlePdfFormResponseChange('newEmployee', e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select...</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                    {pdfFormResponses['newEmployee'] === 'Yes' && (
                      <div className="space-y-2">
                        <Label>Date of Hire</Label>
                        {renderPdfFormField('newHireDate', 'MM/DD/YYYY')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold mb-4">Home Address</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label>Street Address *</Label>
                      {renderPdfFormField('address', 'Street Address')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>City *</Label>
                        {renderPdfFormField('city', 'City')}
                      </div>
                      <div className="space-y-2">
                        <Label>State *</Label>
                        {renderPdfFormField('state', 'State')}
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP Code *</Label>
                        {renderPdfFormField('zipCode', 'ZIP')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold mb-4">Exemptions and Withholding</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Number of Personal and Dependent Exemptions *</Label>
                      {renderPdfFormField('exemptions', '0')}
                    </div>
                    <div className="space-y-2">
                      <Label>Additional Amount to Withhold from Each Pay</Label>
                      {renderPdfFormField('additionalWithholding', '0')}
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold mb-4">Section 8: Claim Exemption from Withholding</h4>
                  <p className="text-sm text-gray-600 mb-4">I claim exemption from withholding because:</p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={pdfFormResponses['exemptTaxLiability'] === 'yes'}
                        onChange={(e) => handlePdfFormResponseChange('exemptTaxLiability', e.target.checked ? 'yes' : 'no')}
                        className="mt-1"
                      />
                      <Label className="font-normal">A Michigan income tax liability is not expected this year.</Label>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={pdfFormResponses['exemptWages'] === 'yes'}
                          onChange={(e) => handlePdfFormResponseChange('exemptWages', e.target.checked ? 'yes' : 'no')}
                          className="mt-1"
                        />
                        <Label className="font-normal">Wages are exempt from withholding. Explain:</Label>
                      </div>
                      {pdfFormResponses['exemptWages'] === 'yes' && (
                        <div className="ml-6">
                          {renderPdfFormField('exemptWagesExplanation', 'Explanation')}
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={pdfFormResponses['exemptRenaissanceZone'] === 'yes'}
                          onChange={(e) => handlePdfFormResponseChange('exemptRenaissanceZone', e.target.checked ? 'yes' : 'no')}
                          className="mt-1"
                        />
                        <Label className="font-normal">Permanent home (domicile) is located in the following Renaissance Zone:</Label>
                      </div>
                      {pdfFormResponses['exemptRenaissanceZone'] === 'yes' && (
                        <div className="ml-6">
                          {renderPdfFormField('renaissanceZone', 'Zone Name')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold mb-4">Signature</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Employee's Signature (Type your full name) *</Label>
                      {renderPdfFormField('signature', 'Your Full Name')}
                    </div>
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      {renderPdfFormField('date', 'MM/DD/YYYY')}
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Ohio IT-4 Form */}
              {selectedPdfForm && (selectedPdfForm.title.toLowerCase().includes('ohio') || selectedPdfForm.title.toLowerCase().includes('it-4') || selectedPdfForm.title.toLowerCase().includes('it4')) && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold">Ohio Form IT-4: Employee's Withholding Exemption Certificate</h3>
                  
                  {/* Section I: Personal Information */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Employee Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Name *</Label>
                        {renderPdfFormField('firstName', 'First Name')}
                      </div>
                      <div className="space-y-2">
                        <Label>Middle Initial</Label>
                        {renderPdfFormField('middleInitial', 'M.I.')}
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name *</Label>
                        {renderPdfFormField('lastName', 'Last Name')}
                      </div>
                      <div className="space-y-2">
                        <Label>Social Security Number *</Label>
                        {renderPdfFormField('ssn', 'XXX-XX-XXXX')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Home Address (Number and Street) *</Label>
                        {renderPdfFormField('homeAddress', 'Street Address')}
                      </div>
                      <div className="space-y-2">
                        <Label>City *</Label>
                        {renderPdfFormField('city', 'City')}
                      </div>
                      <div className="space-y-2">
                        <Label>State *</Label>
                        {renderPdfFormField('state', 'State')}
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP Code *</Label>
                        {renderPdfFormField('zipCode', 'ZIP Code')}
                      </div>
                    </div>
                  </div>

                  {/* School District Information */}
                  <div className="border rounded-lg p-4 bg-orange-50">
                    <h4 className="font-semibold text-lg mb-4">School District Information</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Enter your school district of residence and school district number. You can find your school district at: 
                      <a href="https://tax.ohio.gov/wps/portal/gov/tax/individual/resources/school-district-income-tax" 
                         target="_blank" 
                         rel="noopener noreferrer" 
                         className="text-orange-600 underline ml-1">
                        Ohio School District Lookup
                      </a>
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>School District of Residence *</Label>
                        {renderPdfFormField('schoolDistrict', 'School District Name')}
                      </div>
                      <div className="space-y-2">
                        <Label>School District Number *</Label>
                        {renderPdfFormField('schoolDistrictNumber', '4-digit number')}
                      </div>
                    </div>
                  </div>

                  {/* Section II: Withholding Exemptions */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Section II: Withholding Exemptions</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Complete lines 1 through 4. Enter the total on line 4.
                    </p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                        <div className="md:col-span-10">
                          <Label>1. Personal exemption. Enter "1" for yourself. If you can be claimed as a dependent on someone else's tax return, enter "0".</Label>
                        </div>
                        <div className="md:col-span-2">
                          <Input
                            type="number"
                            min="0"
                            max="1"
                            placeholder="0 or 1"
                            value={pdfFormResponses['exemption1'] || ''}
                            onChange={(e) => handlePdfFormResponseChange('exemption1', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                        <div className="md:col-span-10">
                          <Label>2. Spousal exemption. Enter "1" if you are married and your spouse does not have income subject to Ohio withholding. Otherwise, enter "0".</Label>
                        </div>
                        <div className="md:col-span-2">
                          <Input
                            type="number"
                            min="0"
                            max="1"
                            placeholder="0 or 1"
                            value={pdfFormResponses['exemption2'] || ''}
                            onChange={(e) => handlePdfFormResponseChange('exemption2', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                        <div className="md:col-span-10">
                          <Label>3. Dependent exemptions. Enter the number of dependents you are entitled to claim.</Label>
                        </div>
                        <div className="md:col-span-2">
                          <Input
                            type="number"
                            min="0"
                            placeholder="Number"
                            value={pdfFormResponses['exemption3'] || ''}
                            onChange={(e) => handlePdfFormResponseChange('exemption3', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center bg-orange-100 p-3 rounded-lg">
                        <div className="md:col-span-10">
                          <Label className="font-bold">4. TOTAL EXEMPTIONS (Add lines 1, 2, and 3)</Label>
                        </div>
                        <div className="md:col-span-2">
                          <Input
                            type="number"
                            min="0"
                            placeholder="Total"
                            value={pdfFormResponses['totalExemptions'] || ''}
                            onChange={(e) => handlePdfFormResponseChange('totalExemptions', e.target.value)}
                            className="font-bold"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center pt-2 border-t">
                        <div className="md:col-span-10">
                          <Label>5. Additional withholding. Enter any additional Ohio income tax you want withheld from each pay period (optional).</Label>
                        </div>
                        <div className="md:col-span-2">
                          <Input
                            type="text"
                            placeholder="$0.00"
                            value={pdfFormResponses['additionalWithholding'] || ''}
                            onChange={(e) => handlePdfFormResponseChange('additionalWithholding', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section III: Exemption from Withholding */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-lg mb-4">Section III: Exemption from Withholding</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>Check the box(es) below ONLY if you meet one of the exemption criteria.</strong> If you check any box, your employer will not withhold Ohio income tax or school district income tax from your pay.
                    </p>
                    <div className="space-y-3 bg-white border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="mt-1 w-5 h-5 accent-orange-500"
                          checked={pdfFormResponses['exemptReciprocity'] === 'yes'}
                          onChange={(e) => handlePdfFormResponseChange('exemptReciprocity', e.target.checked ? 'yes' : 'no')}
                        />
                        <div>
                          <span className="text-sm font-medium">Reciprocal State Resident</span>
                          <p className="text-xs text-gray-500">I am a full-year resident of Indiana, Kentucky, Michigan, Pennsylvania, or West Virginia.</p>
                        </div>
                      </label>
                      <label className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="mt-1 w-5 h-5 accent-orange-500"
                          checked={pdfFormResponses['exemptMilitaryOut'] === 'yes'}
                          onChange={(e) => handlePdfFormResponseChange('exemptMilitaryOut', e.target.checked ? 'yes' : 'no')}
                        />
                        <div>
                          <span className="text-sm font-medium">Ohio Resident - Military Stationed Outside Ohio</span>
                          <p className="text-xs text-gray-500">I am an Ohio resident on active duty military orders stationed outside Ohio.</p>
                        </div>
                      </label>
                      <label className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="mt-1 w-5 h-5 accent-orange-500"
                          checked={pdfFormResponses['exemptMilitaryIn'] === 'yes'}
                          onChange={(e) => handlePdfFormResponseChange('exemptMilitaryIn', e.target.checked ? 'yes' : 'no')}
                        />
                        <div>
                          <span className="text-sm font-medium">Nonresident Military Stationed in Ohio</span>
                          <p className="text-xs text-gray-500">I am a nonresident military servicemember stationed in Ohio due to military orders.</p>
                        </div>
                      </label>
                      <label className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="mt-1 w-5 h-5 accent-orange-500"
                          checked={pdfFormResponses['exemptMilitarySpouse'] === 'yes'}
                          onChange={(e) => handlePdfFormResponseChange('exemptMilitarySpouse', e.target.checked ? 'yes' : 'no')}
                        />
                        <div>
                          <span className="text-sm font-medium">Military Spouse</span>
                          <p className="text-xs text-gray-500">I am a nonresident civilian spouse of a military servicemember and I am present in Ohio solely due to my spouse's military orders.</p>
                        </div>
                      </label>
                      <label className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="mt-1 w-5 h-5 accent-orange-500"
                          checked={pdfFormResponses['exemptRC5747'] === 'yes'}
                          onChange={(e) => handlePdfFormResponseChange('exemptRC5747', e.target.checked ? 'yes' : 'no')}
                        />
                        <div>
                          <span className="text-sm font-medium">Other Exemption</span>
                          <p className="text-xs text-gray-500">I am exempt from Ohio withholding under R.C. 5747.06(A)(1) through (6).</p>
                        </div>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-3 italic">
                      Leave all boxes unchecked if none of these situations apply to you.
                    </p>
                  </div>

                  {/* Signature Section */}
                  <div className="border rounded-lg p-4 bg-orange-50">
                    <h4 className="font-semibold text-lg mb-4">Signature</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Under penalties of perjury, I declare that the information on this certificate is true, correct, and complete.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Employee's Signature (Type your full name) *</Label>
                        {renderPdfFormField('signature', 'Your Full Name')}
                        <p className="text-xs text-gray-500 italic">
                          By typing your name, you are electronically signing this form.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Date (MM/DD/YYYY) *</Label>
                        {renderPdfFormField('signatureDate', 'MM/DD/YYYY')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Generic Form Renderer - For forms not specifically coded above */}
              {selectedPdfForm && 
               !selectedPdfForm.title.toLowerCase().includes('w-4') && 
               !selectedPdfForm.title.toLowerCase().includes('w4') && 
               !selectedPdfForm.title.toLowerCase().includes('i-9') && 
               !selectedPdfForm.title.toLowerCase().includes('i9') && 
               !selectedPdfForm.title.toLowerCase().includes('ohio') && 
               !selectedPdfForm.title.toLowerCase().includes('it-4') && 
               !selectedPdfForm.title.toLowerCase().includes('it4') && 
               !selectedPdfForm.title.toLowerCase().includes('mi-w4') && 
               !selectedPdfForm.title.toLowerCase().includes('michigan') && 
               selectedPdfForm.fields && 
               selectedPdfForm.fields.length > 0 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold">{selectedPdfForm.title}</h3>
                  
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedPdfForm.fields.map((field, index) => (
                        <div key={field.name || field.label || `field-${index}`} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                          <Label>
                            {field.label} {field.required && '*'}
                          </Label>
                          {field.type === 'select' && field.options ? (
                            <Select
                              value={pdfFormResponses[field.name] || ''}
                              onValueChange={(value) => handlePdfFormResponseChange(field.name, value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`Select ${field.label}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options.map((option, optIdx) => (
                                  <SelectItem key={`${option}-${optIdx}`} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : field.type === 'textarea' ? (
                            <Textarea
                              value={pdfFormResponses[field.name] || ''}
                              onChange={(e) => handlePdfFormResponseChange(field.name, e.target.value)}
                              placeholder={field.label}
                              className="mt-1"
                            />
                          ) : field.type === 'checkbox' ? (
                            <div className="flex items-center space-x-2 mt-1">
                              <Checkbox
                                checked={pdfFormResponses[field.name] === true || pdfFormResponses[field.name] === 'true'}
                                onCheckedChange={(checked) => handlePdfFormResponseChange(field.name, checked)}
                              />
                              <Label className="font-normal">{field.label}</Label>
                            </div>
                          ) : (
                            <Input
                              type={field.type || 'text'}
                              value={pdfFormResponses[field.name] || ''}
                              onChange={(e) => handlePdfFormResponseChange(field.name, e.target.value)}
                              placeholder={field.label}
                              className="mt-1"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPdfFormDialog(false);
                setSelectedPdfForm(null);
                setPdfFormResponses({});
              }}
            >
              Cancel
            </Button>
            <Button onClick={submitPdfForm} className="bg-blue-600 hover:bg-blue-700">
              Submit Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerOnboarding;